import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { onboardingQuestionnaireActions } from '../../../../../../store';
import { DESKTOP_MOBILE_CAPTURE_DOCUMENT_POLL_INTERVAL_SECONDS } from '../constants';

import type { AppDispatch, RootState } from '../../../../../../store';
import type { IOnboardingQuestionnaireDocument } from '../../../../onboarding/types';

export default function usePollOnboardingDocuments(enabled: boolean) {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { onboardingQuestionnaireId } = useParams<'onboardingQuestionnaireId'>();
  const [isPolling, setIsPolling] = useState(false);
  const documents = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.onboardingQuestionnaireDetails?.documents,
  );
  const documentsRef = useRef<IOnboardingQuestionnaireDocument[] | undefined>(documents);
  const isPollInFlightRef = useRef(false);

  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  useEffect(() => {
    if (!enabled || !onboardingQuestionnaireId) {
      return;
    }

    let cancelled = false;

    const { getOnboardingQuestionnaireDocumentDetails, getOnboardingQuestionnaireDocuments } =
      onboardingQuestionnaireActions;

    const pollDocuments = async () => {
      if (cancelled || isPollInFlightRef.current) {
        return;
      }

      isPollInFlightRef.current = true;
      setIsPolling(true);

      try {
        const settledDocumentIds = new Set(
          documentsRef.current
            ?.filter((document) => document.document_url)
            .map((document) => document.id) ?? [],
        );
        const result = await dispatch(
          getOnboardingQuestionnaireDocuments({
            onboardingQuestionnaireId,
            getAccessTokenSilently,
          }),
        );

        if (cancelled || !getOnboardingQuestionnaireDocuments.fulfilled.match(result)) {
          return;
        }

        await Promise.allSettled(
          result.payload
            .filter(
              (document) => !settledDocumentIds.has(document.id) && !document.document_url,
            )
            .map((document) =>
              dispatch(
                getOnboardingQuestionnaireDocumentDetails({
                  onboardingQuestionnaireId,
                  documentId: String(document.id),
                  getAccessTokenSilently,
                }),
              ),
            ),
        );
      } finally {
        isPollInFlightRef.current = false;
        if (!cancelled) {
          setIsPolling(false);
        }
      }
    };

    void pollDocuments();
    const intervalId = window.setInterval(() => {
      void pollDocuments();
    }, DESKTOP_MOBILE_CAPTURE_DOCUMENT_POLL_INTERVAL_SECONDS * 1000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      isPollInFlightRef.current = false;
      setIsPolling(false);
    };
  }, [dispatch, enabled, getAccessTokenSilently, onboardingQuestionnaireId]);

  return isPolling;
}
